
export abstract class UrlUtilityService {
  private static readonly PATTERN_URL_PREFIX: RegExp = /^.*?#/g;
  private static readonly PATTERN_URL_PARAMS: RegExp = /\?\S*/g;
  private static readonly PATTERN_PARENT_URL: RegExp = /^\/(page|modal)\/(\w+)/g;
  private static readonly PATTERN_QUERY_STRING: string = '=([^&#]*|&|#|$)';

  static getIntegerParam(parameter: string | undefined): number | undefined {
    const result = this.getStringParam(parameter);
    return result !== undefined ? parseInt(result, 10) : undefined;
  }

  static getStringParam(parameter: string | undefined): string | undefined {
    return parameter !== undefined ? decodeURIComponent(parameter) : undefined;
  }

  /**
   * Extract the state url by removing the url prefix and suffix
   * e.g: /waw/brk/wb/wbr/static/main/index.html#/page/platform/default?param=p1 --> /page/platform/default
   *
   * @param configUrl - a config url may have prefix and/or suffix (params)
   * @return
   */
  static stripToStateUrl(configUrl: string): string {
    return configUrl.replace(UrlUtilityService.PATTERN_URL_PREFIX, '').replace(UrlUtilityService.PATTERN_URL_PARAMS, '');
  }

  /**
   *
   * @param configUrl - a config url may have prefix and/or suffix (params)
   * @return true if the configUrl is indeed a config url
   */
  static isStateUrl(configUrl: string): boolean {
    return Boolean(configUrl.match(UrlUtilityService.PATTERN_URL_PREFIX));
  }

  /**
   * @param stateUrl - a valid state url, such as /page/home/some/long/path or /modal/messages/long/path
   * @return  stripped state url that contains only the abstract/parent path or the original value if bad input
   *
   * e.g /page/home/some/long/path => /page/home
   * e.g /modal/messages/long/path => /modal/messages
   */
  static stripToParentStateUrl(stateUrl: string): string {
    const match: Array<string> = stateUrl.match(UrlUtilityService.PATTERN_PARENT_URL);

    if (match !== null && match.length > 0) {
      return match[0];
    }
    return stateUrl;
  }

  /**
   * getQueryStringValues
   * Returns all (or none) matches on an URL based on searched token
   *   1st Capturing Group (=[^&#]*|&|#|$)
   *     1st Alternative =[^&#]*
   *      = matches the character = literally
   *      Match a single character not present in the list below [^&#]*
   *      * Quantifier — Matches between zero and unlimited times, as many times as possible, giving back as needed (greedy)
   *      &# matches a single character in the list &#
   *     2nd Alternative &
   *      & matches the character & literally
   *     3rd Alternative #
   *      # matches the character # literally
   *     4th Alternative $
   *      $ asserts position at the end of the string, or before the line terminator right at the end of the string (if any)
   *   Global pattern flags
   *    g modifier: global. All matches (don't return after first match)
   *    i modifier: insensitive. Case insensitive match (ignores case of [a-zA-Z])
   *
   * @param url - string containing values in the format x/y/z?a=b&c=d&a=e
   * @param name - a parameter key to look for, for instance 'a'
   * @return - the array of found values, in the example above, ['b', 'e']
   */
  static getQueryStringParams(url: string, name: string): Array<string> {
    const regex = new RegExp(name + UrlUtilityService.PATTERN_QUERY_STRING, 'gi');
    const found = [];
    let item;
    while ((item = regex.exec(url)) !== null) {
      found.push(item[0].split('=')[1]);
    }
    return found;
  }

  static getQueryStringParam(url: string, name: string): string {
    const values = UrlUtilityService.getQueryStringParams(url, name);
    if (values.length > 0) {
      return values[0];
    }
  }
}
